import { OtsDateTime } from '../components/OtsDateTime';
import { OtsActionButton } from '../components/OtsButton';
import {
  getFormattedDateTime,
  type RrcEp10Document,
} from '@ots-frontend/ots-data';
import {
  DescriptionItem,
  DescriptionList,
} from '../components/DescriptionList';
import {
  OtsDataTable,
  OtsDataTableHead,
  OtsDataTableBody,
  OtsDataTableRow,
  OtsDataTableHeaderCell,
  OtsDataTableCell,
} from '../components/OtsDataTable';
import { OtsTablePanel } from '../components/OtsTablePanel';
import { OtsDataTableLink } from '../components/OtsDataTableCells';
import { AdminPanel } from '../components/AdminPanel';

export const complianceDate = (value?: number) =>
  value == null
    ? '—'
    : getFormattedDateTime(value, true, false, { showTimeZone: true });

/** Inspect the selected snapshot; current processing status is displayed separately from this document. */
export const RrcEp10ManifestDetails = ({
  manifest,
  compact,
  onOpenCompanyProfile,
  onOpenLocationProfile,
  onOpenWasteProfile,
}: {
  manifest: RrcEp10Document;
  compact?: boolean;
  onOpenCompanyProfile?: (id: string) => void;
  onOpenLocationProfile?: (id: string) => void;
  onOpenWasteProfile?: (id: string) => void;
}) => {
  const wastes = ([1, 2, 3, 4, 5] as const)
    .map((index) => ({
      index,
      id: manifest[`waste${index}Id`],
      profileId: manifest[`waste${index}RrcEp9WasteProfileId`],
      type: manifest[`waste${index}Type`],
      quantity: manifest[`waste${index}Quantity`],
      unit: manifest[`waste${index}QuantityUnit`],
      exempt: manifest[`waste${index}RcraExempt`],
    }))
    .filter(
      (waste) =>
        waste.profileId || waste.id || waste.type || waste.quantity != null
    );
  return (
    <div className="@container space-y-4">
      <DescriptionList compact={compact} responsive="container">
        <DescriptionItem label="Manifest date">
          <OtsDateTime value={manifest.manifestDttm} showTimeZone />
        </DescriptionItem>
        <DescriptionItem label="Created">
          <OtsDateTime value={manifest.createDttm} showTimeZone />
        </DescriptionItem>
        <DescriptionItem label="Updated">
          <OtsDateTime value={manifest.lastModifiedDttm} showTimeZone />
        </DescriptionItem>
        <DescriptionItem label="Waste description">
          {manifest.wasteDescription}
        </DescriptionItem>
        <DescriptionItem label="NORM reading (µR/hr)">
          {manifest.wasteNormReading ?? '—'}
        </DescriptionItem>
      </DescriptionList>
      <OtsTablePanel title="Waste" compact={compact} fill={false}>
        <OtsDataTable
          aria-label="Manifest waste"
          density={compact ? 'compact' : 'default'}
          contained={false}
        >
          <OtsDataTableHead>
            <OtsDataTableRow>
              {['Waste ID', 'Type', 'Quantity', 'Unit', 'RCRA exempt'].map(
                (label) => (
                  <OtsDataTableHeaderCell key={label}>
                    {label}
                  </OtsDataTableHeaderCell>
                )
              )}
            </OtsDataTableRow>
          </OtsDataTableHead>
          <OtsDataTableBody>
            {wastes.length ? (
              wastes.map((waste) => (
                <OtsDataTableRow key={waste.index}>
                  <OtsDataTableCell>
                    {onOpenWasteProfile && waste.profileId ? (
                      <OtsDataTableLink
                        ariaLabel={`Open EP-9 profile for waste ${
                          waste.id ?? waste.index
                        }`}
                        onClick={() => onOpenWasteProfile(waste.profileId!)}
                      >
                        {waste.id || 'EP-9 profile'}
                      </OtsDataTableLink>
                    ) : (
                      waste.id ?? '—'
                    )}
                  </OtsDataTableCell>
                  <OtsDataTableCell>{waste.type ?? '—'}</OtsDataTableCell>
                  <OtsDataTableCell>{waste.quantity ?? '—'}</OtsDataTableCell>
                  <OtsDataTableCell>{waste.unit ?? '—'}</OtsDataTableCell>
                  <OtsDataTableCell>
                    {waste.exempt == null ? '—' : waste.exempt ? 'Yes' : 'No'}
                  </OtsDataTableCell>
                </OtsDataTableRow>
              ))
            ) : (
              <OtsDataTableRow>
                <OtsDataTableCell colSpan={5}>
                  No waste details recorded.
                </OtsDataTableCell>
              </OtsDataTableRow>
            )}
          </OtsDataTableBody>
        </OtsDataTable>
      </OtsTablePanel>
      {(['generator', 'transporter', 'receiver'] as const).map((role) => (
        <AdminPanel
          compact={compact}
          key={role}
          actions={
            <div className="flex flex-wrap gap-2">
              {onOpenCompanyProfile &&
                manifest[`${role}RrcCompanyProfileId`] && (
                  <OtsActionButton
                    size="compact"
                    onClick={() =>
                      onOpenCompanyProfile(
                        manifest[`${role}RrcCompanyProfileId`]!
                      )
                    }
                  >
                    Open company profile
                  </OtsActionButton>
                )}
              {role !== 'transporter' &&
                onOpenLocationProfile &&
                manifest[`${role}RrcLocationProfileId`] && (
                  <OtsActionButton
                    size="compact"
                    onClick={() =>
                      onOpenLocationProfile(
                        manifest[`${role}RrcLocationProfileId`]!
                      )
                    }
                  >
                    Open location profile
                  </OtsActionButton>
                )}
            </div>
          }
          title={role.charAt(0).toUpperCase() + role.slice(1)}
          bodyClassName="@container p-4"
        >
          <DescriptionList compact={compact} responsive="container">
            <DescriptionItem label="Organization">
              {manifest[`${role}OrgName`]}
            </DescriptionItem>
            <DescriptionItem label="Contact">
              {manifest[`${role}ContactName`]}
            </DescriptionItem>
            <DescriptionItem label="Phone">
              {manifest[`${role}ContactPhone`]}
            </DescriptionItem>
            <DescriptionItem label="Email">
              {manifest[`${role}ContactEmail`]}
            </DescriptionItem>
            {role === 'generator' && (
              <>
                <DescriptionItem label="P5 number">
                  {manifest.generatorP5Number}
                </DescriptionItem>
                <DescriptionItem label="Facility">
                  {manifest.generatorLocationFacilityName}
                </DescriptionItem>
                <DescriptionItem label="Lease">
                  {manifest.generatorLocationLeaseName}
                </DescriptionItem>
                <DescriptionItem label="Lease number">
                  {manifest.generatorLocationLeaseNumber}
                </DescriptionItem>
                <DescriptionItem label="API number">
                  {manifest.generatorLocationApiNumber}
                </DescriptionItem>
                <DescriptionItem label="Gas plant number">
                  {manifest.generatorLocationGasPlantNumber}
                </DescriptionItem>
                <DescriptionItem label="Other identifier">
                  {manifest.generatorLocationOtherIdentifier}
                </DescriptionItem>
                <DescriptionItem label="County">
                  {manifest.generatorLocationCounty}
                </DescriptionItem>
                <DescriptionItem label="Coordinates">
                  {manifest.generatorLocationLatCoord != null &&
                  manifest.generatorLocationLongCoord != null
                    ? `${manifest.generatorLocationLatCoord}, ${manifest.generatorLocationLongCoord}`
                    : '—'}
                </DescriptionItem>
              </>
            )}
            {role === 'transporter' && (
              <>
                <DescriptionItem label="P5 number">
                  {manifest.transporterP5Number}
                </DescriptionItem>
                <DescriptionItem label="Permit number">
                  {manifest.transporterPermitNumber}
                </DescriptionItem>
                <DescriptionItem label="Discrepancy">
                  {manifest.transporterDiscrepancyType}
                </DescriptionItem>
                <DescriptionItem label="Discrepancy details">
                  {manifest.transporterDiscrepancyDetails}
                </DescriptionItem>
              </>
            )}
            {role === 'receiver' && (
              <>
                <DescriptionItem label="Facility">
                  {manifest.receiverLocationFacilityName}
                </DescriptionItem>
                <DescriptionItem label="Facility permit">
                  {manifest.receiverLocationFacilityPermitNumber}
                </DescriptionItem>
                <DescriptionItem label="Accepted">
                  <OtsDateTime
                    value={manifest.receiverAcceptanceDttm}
                    showTimeZone
                  />
                </DescriptionItem>
                <DescriptionItem label="Paint filter passed">
                  {manifest.receiverAcceptancePaintFilterPass == null
                    ? '—'
                    : manifest.receiverAcceptancePaintFilterPass
                    ? 'Yes'
                    : 'No'}
                </DescriptionItem>
                <DescriptionItem label="Acceptance NORM reading">
                  {manifest.receiverAcceptanceNormReading ?? '—'}
                </DescriptionItem>
                <DescriptionItem label="Discrepancy">
                  {manifest.receiverDiscrepancyType}
                </DescriptionItem>
                <DescriptionItem label="Discrepancy details">
                  {manifest.receiverDiscrepancyDetails}
                </DescriptionItem>
              </>
            )}
            <DescriptionItem label="Certified by">
              {manifest[`${role}SignedByName`]}
            </DescriptionItem>
            <DescriptionItem label="Certified at">
              <OtsDateTime value={manifest[`${role}SignedDttm`]} showTimeZone />
            </DescriptionItem>
          </DescriptionList>
        </AdminPanel>
      ))}
    </div>
  );
};
